import { LogOut, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Environment } from '@/api/apiTypes';
import { useDrawer } from '@/components/Drawer/context/useDrawer';
import { topbarActions } from '@/config/navigation';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useAuth } from '@/providers/useAuth';
import { useEnvironmentScope } from '@/providers/useEnvironmentScope';
import '@/components/layout/topbar/Topbar.scss';

const EMPTY_ENVIRONMENTS: Environment[] = [];

export function Topbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeDrawer, closeDrawer, openDrawer } = useDrawer();
  const { user, logout } = useAuth();
  const { environmentScope, setEnvironmentScope } = useEnvironmentScope();
  const environmentsQuery = useEnvironments();
  const environments = environmentsQuery.data ?? EMPTY_ENVIRONMENTS;
  const showEnvironmentScope = !location.pathname.startsWith('/tenants/');

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || t('topbar.adminUser');

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="topbar">
      {showEnvironmentScope ? (
        <div className="topbar__scope-slot">
          <label className="topbar__environment-field">
            <span className="topbar__environment-label">{t('topbar.environmentScope')}</span>
            <select
              className="field__control topbar__environment-select"
              aria-label={t('topbar.environmentScope')}
              value={environmentScope}
              onChange={(event) => setEnvironmentScope(event.target.value as 'all' | `${number}`)}
              disabled={environmentsQuery.isPending || environments.length === 0}
            >
              <option value="all">{t('dashboard.scope.allAssigned')}</option>
              {environments.map((environment) => (
                <option key={environment.id} value={String(environment.id) as `${number}`}>
                  {environment.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      <div className="topbar__actions">
        <div className="topbar__profile">
          <span className="topbar__profile-avatar" aria-hidden="true">
            <UserRound size={18} />
          </span>
          <div className="topbar__profile-meta">
            <span>{displayName}</span>
            <small>{user?.email || t('topbar.account')}</small>
          </div>
        </div>

        <div className="topbar__action-icons" aria-label="Topbar actions">
          <button
            type="button"
            className="icon-button"
            onClick={() => void handleLogout()}
            aria-label={t('topbar.logout')}
          >
            <LogOut size={18} />
          </button>

          {topbarActions.map(({ id, labelKey, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className="icon-button"
              onClick={() => {
                if (activeDrawer === id) {
                  closeDrawer();
                  return;
                }

                openDrawer(id);
              }}
              aria-label={t(labelKey)}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
